/**
 * The match review's pure model (deliverables 16 and 18): the words for choices, tiers and
 * re-import actions, which choices a page offers, the choices a set of pages shares, the filters
 * and their place in the URL. The client never derives a default from the tier; a page is
 * undecided when the server left its `choice` null.
 */
import {
  IMPORT_CHOICES,
  IMPORT_CHOICES_BY_TIER,
  IMPORT_MATCH_TIERS,
  IMPORT_REIMPORT_ACTIONS,
  type ImportChoice,
  type ImportMatchTier,
  type ImportPagesFilterV1,
  type ImportPageV1,
  type ImportReimportAction,
} from '@codex/contracts';

export { CHOICE_LABELS } from './choices.js';

/** The tier in words (deliverable 17). */
export const TIER_WORDS: Readonly<Record<ImportMatchTier, string>> = {
  mapped: 'Already imported',
  stub: 'Matches a stub',
  title: 'Same title as a page here',
  none: 'New',
};

/** The re-import action as a badge (deliverable 17). */
export const REIMPORT_WORDS: Readonly<Record<ImportReimportAction, string>> = {
  unchanged: 'Unchanged',
  update: 'Changed in Notion',
  changed_in_both: 'Changed in both',
  fill_stub: 'Fills a stub',
  target_gone: 'Page removed here',
  gone: 'Gone from Notion',
  stub_unresolved: 'Stub not in this export',
};

/**
 * What a re-import action offers, as 2.A's `REIMPORT_CHOICES` does (the web app does not import
 * that package): a page with an action takes these in place of its tier's.
 */
const REIMPORT_CHOICE_LISTS: Readonly<Record<ImportReimportAction, readonly ImportChoice[]>> = {
  unchanged: [],
  update: ['update', 'keep_mine', 'keep_both', 'skip'],
  changed_in_both: ['keep_mine', 'take_notion', 'keep_both', 'skip'],
  fill_stub: ['update', 'skip'],
  target_gone: ['keep_both', 'skip'],
  gone: [],
  stub_unresolved: [],
};

/** The choices a page offers: its re-import action's when it has one, else its tier's. */
export function choicesFor(page: Pick<ImportPageV1, 'tier' | 'reimport'>): readonly ImportChoice[] {
  return page.reimport === null
    ? IMPORT_CHOICES_BY_TIER[page.tier]
    : REIMPORT_CHOICE_LISTS[page.reimport];
}

/** The choices that name a document: the server refuses a target on any other. */
export const TARGETED_CHOICES: ReadonlySet<ImportChoice> = new Set([
  'fill',
  'pair',
  'pair_replace',
]);

/** The target to send with a choice: only a targeted choice carries one. */
export const targetFor = (choice: ImportChoice, target: string | undefined): string | undefined =>
  TARGETED_CHOICES.has(choice) ? target : undefined;

/** Only the choices every one of the pages offers, in `IMPORT_CHOICES` order. */
export function commonChoices(
  pages: readonly Pick<ImportPageV1, 'tier' | 'reimport'>[],
): ImportChoice[] {
  if (pages.length === 0) return [];
  const lists = pages.map(choicesFor);
  return IMPORT_CHOICES.filter((choice) => lists.every((list) => list.includes(choice)));
}

/** True exactly when the server left the page without a choice, whatever its tier. */
export const isUndecided = (page: Pick<ImportPageV1, 'choice'>): boolean => page.choice === null;

/** A page the GM still owes a choice: undecided and offering at least one. */
export const needsChoice = (page: Pick<ImportPageV1, 'choice' | 'tier' | 'reimport'>): boolean =>
  isUndecided(page) && choicesFor(page).length > 0;

// ---- filters ----

/** `needs`: pages still owed a choice; the server cannot filter on `choice: null`. */
export type ChoiceFilter = ImportChoice | 'needs';
/** `all`: every tier; no tier at all means the default view, which leaves out New pages. */
export type TierFilter = ImportMatchTier | 'all';

export interface MatchFilters {
  readonly tier?: TierFilter | undefined;
  readonly choice?: ChoiceFilter | undefined;
  readonly reimport?: ImportReimportAction | undefined;
  readonly typeKey?: string | undefined;
  readonly skippedBefore?: boolean | undefined;
}

export const NO_FILTERS: MatchFilters = {};

const isOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (list as readonly string[]).includes(value);

/**
 * The URL's search keys of the filters: only the ones set. `skipped` is the number 1, never the
 * string '1': the router JSON-quotes a string that parses as JSON, so '1' reached the address as
 * `skipped=%221%22` (gate exit-2a run 2, DECISIONS 597); the address reads `skipped=1`.
 */
export function filtersToQuery(filters: MatchFilters): Record<string, string | 1> {
  const query: Record<string, string | 1> = {};
  if (filters.tier !== undefined) query.tier = filters.tier;
  if (filters.choice !== undefined) query.choice = filters.choice;
  if (filters.reimport !== undefined) query.reimport = filters.reimport;
  if (filters.typeKey !== undefined && filters.typeKey !== '') query.type = filters.typeKey;
  if (filters.skippedBefore === true) query.skipped = 1;
  return query;
}

/** The filters a search holds; a value that is not one of the allowed ones is dropped. */
export function filtersFromSearch(search: Record<string, unknown>): MatchFilters {
  const out: {
    tier?: TierFilter;
    choice?: ChoiceFilter;
    reimport?: ImportReimportAction;
    typeKey?: string;
    skippedBefore?: boolean;
  } = {};
  if (isOf([...IMPORT_MATCH_TIERS, 'all'] as const, search.tier)) out.tier = search.tier;
  if (isOf([...IMPORT_CHOICES, 'needs'] as const, search.choice)) out.choice = search.choice;
  if (isOf(IMPORT_REIMPORT_ACTIONS, search.reimport)) out.reimport = search.reimport;
  if (typeof search.type === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(search.type)) {
    out.typeKey = search.type;
  }
  if (search.skipped === '1' || search.skipped === 1 || search.skipped === true) {
    out.skippedBefore = true;
  }
  return out;
}

/** The part of the filters the server understands (`import.pages.list` and `resolve`). */
export function serverFilter(filters: MatchFilters): ImportPagesFilterV1 {
  const out: {
    tier?: ImportMatchTier;
    choice?: ImportChoice;
    reimport?: ImportReimportAction;
    typeKey?: string;
    skippedBefore?: boolean;
  } = {};
  if (filters.tier !== undefined && filters.tier !== 'all') out.tier = filters.tier;
  if (filters.choice !== undefined && filters.choice !== 'needs') out.choice = filters.choice;
  if (filters.reimport !== undefined) out.reimport = filters.reimport;
  if (filters.typeKey !== undefined && filters.typeKey !== '') out.typeKey = filters.typeKey;
  if (filters.skippedBefore === true) out.skippedBefore = true;
  return out;
}

/** True when the server's filter cannot say everything the filters do (the client trims a page). */
export const needsClientFilter = (filters: MatchFilters): boolean =>
  filters.tier === undefined || filters.choice === 'needs';

/**
 * The part of the filters the client applies to what the server sent: the default view leaves
 * out New pages, and "needs a choice" keeps the undecided ones.
 */
export function matchesClient(
  page: Pick<ImportPageV1, 'tier' | 'choice' | 'reimport'>,
  filters: MatchFilters,
): boolean {
  if (filters.tier === undefined && page.tier === 'none') return false;
  if (filters.choice === 'needs' && !needsChoice(page)) return false;
  return true;
}

/** True when any filter narrows the default view. */
export const hasFilters = (filters: MatchFilters): boolean =>
  Object.keys(filtersToQuery(filters)).length > 0;

/** A stable text for a set of filters (query keys). */
export const filtersKey = (filters: MatchFilters): string =>
  JSON.stringify(Object.entries(filtersToQuery(filters)).sort());

/** Ids of the chunks a bulk request is cut into: at most 500 per request. */
export const BULK_CHUNK = 500;

export function chunk<T>(items: readonly T[], size: number = BULK_CHUNK): T[][] {
  const out: T[][] = [];
  for (let at = 0; at < items.length; at += size) out.push(items.slice(at, at + size));
  return out;
}

/** The segment of a page that is the mapping's default for its variant, if any. */
export function defaultMarked(
  page: Pick<ImportPageV1, 'tier' | 'reimport'>,
  defaults: { readonly pair: ImportChoice; readonly changedInBoth: ImportChoice },
): ImportChoice | null {
  if (page.tier === 'title') return defaults.pair;
  if (page.reimport === 'changed_in_both') return defaults.changedInBoth;
  return null;
}
